import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { settingsApi } from '../services/settings';
import {
  productsApi,
  type Product,
  type ProductComponent,
  type ProductDetail,
  type ProductFormData,
  type ProductSalesPriceFormData,
  type ProductSalesPriceMeta,
  type ProductSalesPriceRow,
} from '../services/products';
import { useCallback, useEffect, useMemo, useState } from 'react';

type ProductComponentFormRow = {
  componentProductId: number | '';
  qty: string;
  componentSearch: string;
};

type SalesPriceFormState = {
  priceListId: string;
  customerId: string;
  unitPriceExVat: string;
  vatRateItemId: string;
  validFrom: string;
  validTo: string;
  minQty: string;
  notes: string;
};

const EMPTY_FORM: ProductFormData = {
  name: '',
  kind: 'BULK',
  parentProductId: null,
  categoryItemId: null,
  defaultUomItemId: null,
  defaultVatRateItemId: null,
  netWeightG: null,
  canPurchase: true,
  canSell: true,
  salesPriceRetailIncVat: null,
  salesPriceDefaultExVat: null,
  syncWooCommerce: false,
  syncZettle: false,
  syncGs1: false,
  syncFortnox: false,
  metaJson: null,
  notes: '',
  sku: '',
  ean: '',
  components: [],
};

const EMPTY_COMPONENT_ROW: ProductComponentFormRow = {
  componentProductId: '',
  qty: '1',
  componentSearch: '',
};

const EMPTY_SALES_PRICE_FORM: SalesPriceFormState = {
  priceListId: '',
  customerId: '',
  unitPriceExVat: '',
  vatRateItemId: '',
  validFrom: '',
  validTo: '',
  minQty: '',
  notes: '',
};

function formatComponentRows(components: ProductComponent[] | undefined): ProductComponentFormRow[] {
  if (!components || components.length === 0) {
    return [];
  }

  return components.map((component) => ({
    componentProductId: component.componentProductId,
    qty:
      component.componentUomLabel === 'st'
        ? String(Math.round(component.qty))
        : component.qty
          ? String(component.qty)
          : '',
    componentSearch: `${component.componentSku ?? '-'} - ${component.componentName ?? ''}`.trim(),
  }));
}

function preparationMetaFromForm(form: ProductFormData) {
  return form.metaJson?.preparation ?? {
    legacyPrepId: null,
    withdrawalDays: null,
    treatmentLengthDays: null,
    instructionUrl: '',
  };
}

function formatPrice(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return String(value);
  }
  return `${numberValue.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

function formatQty(value: string | number | null | undefined, uomLabel?: string | null): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return String(value);
  }
  return `${numberValue.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}${uomLabel ? ` ${uomLabel}` : ''}`;
}

function formatDate(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  return value.slice(0, 16).replace('T', ' ');
}

function buildSalesPriceForm(row: ProductSalesPriceRow): SalesPriceFormState {
  return {
    priceListId: String(row.priceListId),
    customerId: row.customerId !== null ? String(row.customerId) : '',
    unitPriceExVat: row.unitPriceExVat ?? '',
    vatRateItemId: row.vatRateItemId !== null ? String(row.vatRateItemId) : '',
    validFrom: row.validFrom ?? '',
    validTo: row.validTo ?? '',
    minQty: row.minQty ?? '',
    notes: row.notes ?? '',
  };
}

function channelStatus(detail: ProductDetail) {
  return [
    { key: 'woo', label: 'WooCommerce', enabled: !!detail.syncWooCommerce },
    { key: 'zettle', label: 'Zettle', enabled: !!detail.syncZettle },
    { key: 'gs1', label: 'GS1 / EAN', enabled: !!detail.syncGs1 },
    { key: 'fortnox', label: 'Fortnox', enabled: !!detail.syncFortnox },
  ];
}

export function ProductDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const productId = Number(params.id);
  const { canRead, canWrite } = useAuth();
  const toast = useToast();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salesPriceMeta, setSalesPriceMeta] = useState<ProductSalesPriceMeta | null>(null);
  const [salesPriceForm, setSalesPriceForm] = useState<SalesPriceFormState>(EMPTY_SALES_PRICE_FORM);
  const [salesPriceEditing, setSalesPriceEditing] = useState<ProductSalesPriceRow | null>(null);
  const [showSalesPriceForm, setShowSalesPriceForm] = useState(false);
  const [savingSalesPrice, setSavingSalesPrice] = useState(false);
  const [deletingSalesPriceId, setDeletingSalesPriceId] = useState<number | null>(null);
  const [confirmDeleteSalesPriceId, setConfirmDeleteSalesPriceId] = useState<number | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [openComponentSearchIndex, setOpenComponentSearchIndex] = useState<number | null>(null);
  const [valueListItems, setValueListItems] = useState<Record<string, Array<{ id: number; label: string }>>>({});
  const [productOptions, setProductOptions] = useState<Array<Product & { optionLabel: string }>>([]);
  const [form, setForm] = useState<ProductFormData>(EMPTY_FORM);
  const [componentRows, setComponentRows] = useState<ProductComponentFormRow[]>([]);

  const loadProduct = useCallback(async () => {
    try {
      const detail = await productsApi.get(productId);
      setProduct(detail);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda produkt');
    } finally {
      setLoading(false);
    }
  }, [productId, toast]);

  useEffect(() => {
    if (Number.isFinite(productId) && productId > 0) {
      loadProduct();
    }
  }, [loadProduct, productId]);

  const integrations = useMemo(() => (product ? channelStatus(product) : []), [product]);
  const selectedCategoryLabel = useMemo(
    () => (valueListItems.PRODUCT_CATEGORY ?? []).find((item) => item.id === form.categoryItemId)?.label ?? null,
    [form.categoryItemId, valueListItems]
  );
  const isPreparationProduct = selectedCategoryLabel === 'Preparat';

  const loadEditorMeta = useCallback(async () => {
    const [cat, uom, vat, productsRes] = await Promise.all([
      settingsApi.getValueListItems('PRODUCT_CATEGORY'),
      settingsApi.getValueListItems('UOM'),
      settingsApi.getValueListItems('VAT_RATES'),
      productsApi.list(),
    ]);
    setValueListItems({
      PRODUCT_CATEGORY: cat.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
      UOM: uom.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
      VAT_RATES: vat.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
    });
    const uniqueProducts = new Map<number, Product>();
    for (const optionProduct of productsRes.products) {
      if (!uniqueProducts.has(optionProduct.id)) {
        uniqueProducts.set(optionProduct.id, optionProduct);
      }
    }
    setProductOptions(
      Array.from(uniqueProducts.values()).map((optionProduct) => ({
        ...optionProduct,
        optionLabel: `${optionProduct.sku ?? '-'} - ${optionProduct.name}`,
      }))
    );
  }, []);

  const loadSalesPriceMeta = useCallback(async () => {
    const meta = await productsApi.salesPriceMeta(productId);
    setSalesPriceMeta(meta);
  }, [productId]);

  const openEdit = useCallback(async () => {
    setLoadingEditor(true);
    try {
      await loadEditorMeta();
      const detail = await productsApi.get(productId);
      setProduct(detail);
      setForm({
        sku: detail.sku ?? '',
        ean: detail.ean ?? '',
        parentProductId: detail.parentProductId,
        name: detail.name,
        kind: detail.kind,
        categoryItemId: detail.categoryItemId,
        defaultUomItemId: detail.defaultUomItemId,
        netWeightG: detail.netWeightG,
        canPurchase: !!detail.canPurchase,
        canSell: !!detail.canSell,
        salesPriceRetailIncVat: detail.salesPriceRetailIncVat !== null ? Number(detail.salesPriceRetailIncVat) : null,
        salesPriceDefaultExVat: detail.salesPriceDefaultExVat !== null ? Number(detail.salesPriceDefaultExVat) : null,
        syncWooCommerce: !!detail.syncWooCommerce,
        syncZettle: !!detail.syncZettle,
        syncGs1: !!detail.syncGs1,
        syncFortnox: !!detail.syncFortnox,
        defaultVatRateItemId: detail.defaultVatRateItemId,
        metaJson: detail.metaJson ?? null,
        notes: detail.notes ?? '',
        components: detail.components.map((component) => ({
          componentProductId: component.componentProductId,
          qty: component.qty,
        })),
      });
      setComponentRows(formatComponentRows(detail.components));
      setShowDeactivateConfirm(false);
      setShowEditor(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda produkt');
    } finally {
      setLoadingEditor(false);
    }
  }, [loadEditorMeta, productId, toast]);

  const openCreateSalesPrice = useCallback(async () => {
    try {
      if (!salesPriceMeta) {
        await loadSalesPriceMeta();
      }
      setSalesPriceEditing(null);
      setSalesPriceForm(EMPTY_SALES_PRICE_FORM);
      setConfirmDeleteSalesPriceId(null);
      setShowSalesPriceForm(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda prislistans metadata');
    }
  }, [loadSalesPriceMeta, salesPriceMeta, toast]);

  const openEditSalesPrice = useCallback(async (row: ProductSalesPriceRow) => {
    try {
      if (!salesPriceMeta) {
        await loadSalesPriceMeta();
      }
      setSalesPriceEditing(row);
      setSalesPriceForm(buildSalesPriceForm(row));
      setConfirmDeleteSalesPriceId(null);
      setShowSalesPriceForm(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda prislistans metadata');
    }
  }, [loadSalesPriceMeta, salesPriceMeta, toast]);

  const resetEditor = () => {
    setShowEditor(false);
    setShowDeactivateConfirm(false);
    setConfirmDeleteIndex(null);
    setOpenComponentSearchIndex(null);
    setForm(EMPTY_FORM);
    setComponentRows([]);
  };

  const resetSalesPriceForm = () => {
    setShowSalesPriceForm(false);
    setSalesPriceEditing(null);
    setSalesPriceForm(EMPTY_SALES_PRICE_FORM);
    setConfirmDeleteSalesPriceId(null);
  };

  const buildComponentPayload = (): Array<{ componentProductId: number; qty: number }> => {
    return componentRows
      .filter((row) => row.componentProductId !== '' && row.qty.trim() !== '')
      .map((row) => ({
        componentProductId: Number(row.componentProductId),
        qty: Number(row.qty),
      }))
      .filter((row) => row.componentProductId > 0 && row.qty > 0);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Namn krävs');
      return;
    }

    setSaving(true);
    try {
      await productsApi.update(productId, {
        ...form,
        components: buildComponentPayload(),
      });
      toast.success('Produkt uppdaterad');
      resetEditor();
      await loadProduct();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara produkt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSalesPrice = async () => {
    const priceListId = Number(salesPriceForm.priceListId);
    const customerId = salesPriceForm.customerId === '' ? null : Number(salesPriceForm.customerId);
    const unitPriceExVat = Number(salesPriceForm.unitPriceExVat);
    const vatRateItemId = salesPriceForm.vatRateItemId === '' ? null : Number(salesPriceForm.vatRateItemId);
    const minQty = salesPriceForm.minQty === '' ? null : Number(salesPriceForm.minQty);

    if (!Number.isFinite(priceListId) || priceListId <= 0) {
      toast.error('Prislista krävs');
      return;
    }
    if (!Number.isFinite(unitPriceExVat) || unitPriceExVat <= 0) {
      toast.error('Pris exkl. moms måste vara större än 0');
      return;
    }
    if (salesPriceForm.validFrom && salesPriceForm.validTo && salesPriceForm.validTo < salesPriceForm.validFrom) {
      toast.error('Slutdatum kan inte vara före startdatum');
      return;
    }
    if (minQty !== null && (!Number.isFinite(minQty) || minQty <= 0)) {
      toast.error('Minsta antal måste vara större än 0');
      return;
    }

    const payload: ProductSalesPriceFormData = {
      priceListId,
      customerId,
      unitPriceExVat,
      vatRateItemId,
      validFrom: salesPriceForm.validFrom || null,
      validTo: salesPriceForm.validTo || null,
      minQty,
      notes: salesPriceForm.notes.trim(),
    };

    setSavingSalesPrice(true);
    try {
      if (salesPriceEditing) {
        await productsApi.updateSalesPrice(productId, salesPriceEditing.id, payload);
        toast.success('Prisrad uppdaterad');
      } else {
        await productsApi.createSalesPrice(productId, payload);
        toast.success('Prisrad skapad');
      }
      resetSalesPriceForm();
      await loadProduct();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara prisrad');
    } finally {
      setSavingSalesPrice(false);
    }
  };

  const handleDeleteSalesPrice = async (rowId: number) => {
    setDeletingSalesPriceId(rowId);
    try {
      await productsApi.deleteSalesPrice(productId, rowId);
      toast.success('Prisrad borttagen');
      if (salesPriceEditing?.id === rowId) {
        resetSalesPriceForm();
      }
      await loadProduct();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort prisrad');
    } finally {
      setDeletingSalesPriceId(null);
      setConfirmDeleteSalesPriceId(null);
    }
  };

  const handleDelete = async () => {
    setDeletingProduct(true);
    try {
      await productsApi.delete(productId);
      toast.success('Produkt inaktiverad');
      navigate('/products');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte inaktivera produkt');
    } finally {
      setDeletingProduct(false);
      setShowDeactivateConfirm(false);
    }
  };

  const updateComponentRow = (index: number, patch: Partial<ProductComponentFormRow>) => {
    setComponentRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const removeComponentRow = (index: number) => {
    setComponentRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setConfirmDeleteIndex(null);
    setOpenComponentSearchIndex((current) => {
      if (current === null) {
        return null;
      }
      if (current === index) {
        return null;
      }
      return current > index ? current - 1 : current;
    });
  };

  if (!canRead('products')) {
    return <Navigate to="/" replace />;
  }

  if (!Number.isFinite(productId) || productId <= 0) {
    return <Navigate to="/products" replace />;
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Laddar produkt...</p>;
  }

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <Link to="/products" className="text-sm text-primary hover:underline">
            Tillbaka till produkter
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{product.name}</h1>
            <span className="rounded bg-gray-100 px-2 py-1 text-sm text-gray-700">{product.sku ?? '-'}</span>
          </div>
          <div className="text-sm text-gray-600">
            {product.categoryLabel ?? 'Ingen kategori'} • {product.kind} • {product.uomLabel ?? '-'}
          </div>
        </div>
        {canWrite('products') && (
          <button
            type="button"
            onClick={() => void openEdit()}
            className="rounded bg-success px-3 py-2 text-sm font-medium text-white hover:bg-success-hover"
            disabled={loadingEditor}
          >
            {loadingEditor ? 'Laddar...' : 'Redigera'}
          </button>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Privat inkl. moms</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">{formatPrice(product.salesPriceRetailIncVat)}</div>
        </div>
        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Företag exkl. moms</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">{formatPrice(product.salesPriceDefaultExVat)}</div>
        </div>
        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Inköpt totalt</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">{formatQty(product.stats.totalPurchasedQty, product.uomLabel)}</div>
        </div>
        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Levererat totalt</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">{formatQty(product.stats.totalShippedQty, product.uomLabel)}</div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Grunddata</h2>
            <div className="mt-3 grid gap-x-6 gap-y-3 text-sm md:grid-cols-2">
              <div>
                <div className="text-gray-500">Artikelnummer</div>
                <div className="font-medium text-gray-900">{product.sku ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">EAN / GTIN</div>
                <div className="font-medium text-gray-900">
                  {product.ean ?? (product.syncGs1 ? 'Skapas vid nästa sparning' : '-')}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Typ</div>
                <div className="font-medium text-gray-900">{product.kind}</div>
              </div>
              <div>
                <div className="text-gray-500">Kategori</div>
                <div className="font-medium text-gray-900">{product.categoryLabel ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Enhet</div>
                <div className="font-medium text-gray-900">{product.uomLabel ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Moms</div>
                <div className="font-medium text-gray-900">{product.vatLabel ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Nettovikt</div>
                <div className="font-medium text-gray-900">{product.netWeightG ? `${product.netWeightG} g` : '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Huvudartikel</div>
                <div className="font-medium text-gray-900">
                  {product.parentName ? `${product.parentSku ? `${product.parentSku} - ` : ''}${product.parentName}` : '-'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Kan köpas in</div>
                <div className="font-medium text-gray-900">{product.canPurchase ? 'Ja' : 'Nej'}</div>
              </div>
              <div>
                <div className="text-gray-500">Kan säljas</div>
                <div className="font-medium text-gray-900">{product.canSell ? 'Ja' : 'Nej'}</div>
              </div>
            </div>
            {product.notes && (
              <div className="mt-4 border-t border-gray-100 pt-3 text-sm">
                <div className="text-gray-500">Anteckningar</div>
                <div className="mt-1 whitespace-pre-wrap text-gray-900">{product.notes}</div>
              </div>
            )}
          </section>

          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Statistik</h2>
            <div className="mt-3 grid gap-x-6 gap-y-3 text-sm md:grid-cols-2">
              <div>
                <div className="text-gray-500">Inköpsdokument</div>
                <div className="font-medium text-gray-900">{product.stats.purchaseDocumentsCount}</div>
              </div>
              <div>
                <div className="text-gray-500">Senaste inköp</div>
                <div className="font-medium text-gray-900">{formatDate(product.stats.lastPurchaseDate)}</div>
              </div>
              <div>
                <div className="text-gray-500">Order</div>
                <div className="font-medium text-gray-900">{product.stats.orderCount}</div>
              </div>
              <div>
                <div className="text-gray-500">Senaste order</div>
                <div className="font-medium text-gray-900">{formatDate(product.stats.lastOrderDate)}</div>
              </div>
              <div>
                <div className="text-gray-500">Leveranser</div>
                <div className="font-medium text-gray-900">{product.stats.shipmentCount}</div>
              </div>
              <div>
                <div className="text-gray-500">Senaste leverans</div>
                <div className="font-medium text-gray-900">{formatDate(product.stats.lastShipmentDate)}</div>
              </div>
            </div>
          </section>

          <section className="rounded border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Försäljningsprislistor</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Produktens företagspris är standardpriset. Lägg här till kundspecifika prisrader exkl. moms med datum, minsta antal och ev. egen moms.
                </p>
              </div>
              {canWrite('products') && (
                <button
                  type="button"
                  onClick={() => void openCreateSalesPrice()}
                  className="rounded bg-success px-3 py-2 text-sm font-medium text-white hover:bg-success-hover"
                >
                  Ny prisrad
                </button>
              )}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">Standardpris företag exkl. moms</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">{formatPrice(product.salesPriceDefaultExVat)}</div>
                <div className="mt-1 text-xs text-gray-500">Detta pris används som grundpris när ingen kundspecifik rad matchar.</div>
              </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Kundspecifika rader</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">{product.salesPrices.length}</div>
                  <div className="mt-1 text-xs text-gray-500">Varje rad kan kopplas till en prislista, ev. kund, datumintervall, minsta antal och egen momssats.</div>
                </div>
              </div>

            <div className="mt-3">
              <TableWrapper maxHeight="18rem">
                <TableHead>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Prislista</th>
                    <th className="px-2 py-1.5 text-left font-medium">Kund</th>
                    <th className="px-2 py-1.5 text-left font-medium">Pris exkl. moms</th>
                    <th className="px-2 py-1.5 text-left font-medium">Moms</th>
                    <th className="px-2 py-1.5 text-left font-medium">Min antal</th>
                    <th className="px-2 py-1.5 text-left font-medium">Giltig från</th>
                    <th className="px-2 py-1.5 text-left font-medium">Giltig till</th>
                    <th className="px-2 py-1.5 text-left font-medium">Anteckning</th>
                    {canWrite('products') && <th className="px-2 py-1.5 text-left font-medium">Åtgärder</th>}
                  </tr>
                </TableHead>
                <tbody>
                  {product.salesPrices.length === 0 && (
                    <tr>
                      <td colSpan={canWrite('products') ? 9 : 8} className="px-2 py-3 text-sm text-gray-500">
                        Inga kundspecifika försäljningspriser ännu.
                      </td>
                    </tr>
                  )}
                  {product.salesPrices.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="px-2 py-1.5">{row.priceListName}</td>
                      <td className="px-2 py-1.5">{row.customerName ?? '-'}</td>
                      <td className="px-2 py-1.5">{formatPrice(row.unitPriceExVat)}</td>
                      <td className="px-2 py-1.5">{row.vatRateLabel ?? product.vatLabel ?? '-'}</td>
                      <td className="px-2 py-1.5">{row.minQty ?? '-'}</td>
                      <td className="px-2 py-1.5">{formatDate(row.validFrom)}</td>
                      <td className="px-2 py-1.5">{formatDate(row.validTo)}</td>
                      <td className="px-2 py-1.5">{row.notes ?? '-'}</td>
                      {canWrite('products') && (
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void openEditSalesPrice(row)}
                              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                            >
                              Redigera
                            </button>
                            {confirmDeleteSalesPriceId === row.id ? (
                              <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1">
                                <span className="text-xs text-red-700">Ta bort?</span>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteSalesPrice(row.id)}
                                  disabled={deletingSalesPriceId === row.id}
                                  className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                  {deletingSalesPriceId === row.id ? 'Tar bort...' : 'Ja'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteSalesPriceId(null)}
                                  disabled={deletingSalesPriceId === row.id}
                                  className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                >
                                  Nej
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteSalesPriceId(row.id)}
                                className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                              >
                                Ta bort
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </TableWrapper>
            </div>
          </section>

          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Toppkunder</h2>
            <div className="mt-3">
            <TableWrapper maxHeight="18rem">
              <TableHead>
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Kund</th>
                  <th className="px-2 py-1.5 text-left font-medium">Levererat</th>
                  <th className="px-2 py-1.5 text-left font-medium">Leveranser</th>
                  <th className="px-2 py-1.5 text-left font-medium">Senast</th>
                </tr>
              </TableHead>
              <tbody>
                {product.stats.topCustomers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-2 py-3 text-sm text-gray-500">
                      Inga leveranser ännu.
                    </td>
                  </tr>
                )}
                {product.stats.topCustomers.map((row) => (
                  <tr key={`${row.customerId ?? 'unknown'}-${row.customerName}`} className="border-b border-gray-100">
                    <td className="px-2 py-1.5">{row.customerName}</td>
                    <td className="px-2 py-1.5">{formatQty(row.shippedQty, product.uomLabel)}</td>
                    <td className="px-2 py-1.5">{row.shipmentCount}</td>
                    <td className="px-2 py-1.5">{formatDate(row.lastShipDate)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrapper>
            </div>
          </section>
        </div>

        <div className="space-y-4">
          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Integrationer</h2>
            <div className="mt-3 space-y-2">
              {integrations.map((item) => (
                <div key={item.key} className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 text-sm">
                  <span>{item.label}</span>
                  <span className={item.enabled ? 'font-medium text-green-700' : 'text-gray-500'}>
                    {item.enabled ? 'Aktiv' : 'Inte vald'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-gray-500">
              Bilder och kanal-specifik metadata byggs vidare här när WooCommerce, Zettle och GS1 går från preview till riktig push/sync.
            </div>
          </section>

          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Ingående artiklar</h2>
            <div className="mt-3">
            <TableWrapper maxHeight="18rem">
              <TableHead>
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Artikel</th>
                  <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
                  <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
                </tr>
              </TableHead>
              <tbody>
                {product.components.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-2 py-3 text-sm text-gray-500">
                      Inga ingående artiklar registrerade.
                    </td>
                  </tr>
                )}
                {product.components.map((component) => (
                  <tr key={component.id ?? component.componentProductId} className="border-b border-gray-100">
                    <td className="px-2 py-1.5">
                      {(component.componentSku ? `${component.componentSku} - ` : '') + (component.componentName ?? '')}
                    </td>
                    <td className="px-2 py-1.5">{formatQty(component.qty)}</td>
                    <td className="px-2 py-1.5">{component.componentUomLabel ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrapper>
            </div>
          </section>

          <section className="rounded border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">Prishistorik</h2>
            <div className="mt-3">
            <TableWrapper maxHeight="18rem">
              <TableHead>
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Datum</th>
                  <th className="px-2 py-1.5 text-left font-medium">Pris exkl. moms</th>
                  <th className="px-2 py-1.5 text-left font-medium">Notering</th>
                </tr>
              </TableHead>
              <tbody>
                {product.priceHistory.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-2 py-3 text-sm text-gray-500">
                      Ingen prishistorik ännu.
                    </td>
                  </tr>
                )}
                {product.priceHistory.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="px-2 py-1.5">{formatDate(row.changedAt)}</td>
                    <td className="px-2 py-1.5">{formatPrice(row.priceExVat)}</td>
                    <td className="px-2 py-1.5">{row.note ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrapper>
            </div>
          </section>
        </div>
      </div>

      {showSalesPriceForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900">{salesPriceEditing ? 'Redigera prisrad' : 'Ny prisrad'}</h3>
                <p className="text-sm text-gray-600">Kundspecifika priser lagras exkl. moms och överstyr standardpriset för vald kund.</p>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Prislista *</label>
                  <select
                    className="w-full"
                    value={salesPriceForm.priceListId}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, priceListId: event.target.value }))}
                  >
                    <option value="">Välj prislista</option>
                    {(salesPriceMeta?.priceLists ?? []).map((priceList) => (
                      <option key={priceList.id} value={priceList.id}>
                        {priceList.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Kund</label>
                  <select
                    className="w-full"
                    value={salesPriceForm.customerId}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, customerId: event.target.value }))}
                  >
                    <option value="">Ingen kundkoppling</option>
                    {(salesPriceMeta?.customers ?? []).map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Pris exkl. moms *</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="w-full"
                    value={salesPriceForm.unitPriceExVat}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, unitPriceExVat: event.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Moms</label>
                  <select
                    className="w-full"
                    value={salesPriceForm.vatRateItemId}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, vatRateItemId: event.target.value }))}
                  >
                    <option value="">Produktens moms ({product.vatLabel ?? 'saknas'})</option>
                    {(salesPriceMeta?.vatRates ?? []).map((vatRate) => (
                      <option key={vatRate.id} value={vatRate.id}>
                        {vatRate.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Minsta antal</label>
                  <input
                    type="number"
                    min={0}
                    step="0.001"
                    className="w-full"
                    value={salesPriceForm.minQty}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, minQty: event.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Giltig från</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    className="w-full"
                    value={salesPriceForm.validFrom}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, validFrom: event.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Giltig till</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    className="w-full"
                    value={salesPriceForm.validTo}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, validTo: event.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600">Anteckning</label>
                  <textarea
                    className="min-h-[90px] w-full"
                    value={salesPriceForm.notes}
                    onChange={(event) => setSalesPriceForm((current) => ({ ...current, notes: event.target.value }))}
                  />
                </div>
              </div>

              {salesPriceEditing && (
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  Senast uppdaterad: {formatDateTime(salesPriceEditing.updatedAt)}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSaveSalesPrice}
                  disabled={savingSalesPrice}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {savingSalesPrice ? 'Sparar...' : 'Spara prisrad'}
                </button>
                <button
                  type="button"
                  onClick={resetSalesPriceForm}
                  disabled={savingSalesPrice}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100 disabled:opacity-50"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <h3 className="font-semibold">Redigera produkt</h3>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Namn *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Artikelnummer</label>
                  <input
                    value={form.sku ?? ''}
                    onChange={(e) => setForm((current) => ({ ...current, sku: e.target.value }))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">EAN / GTIN</label>
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    {form.syncGs1 ? (form.ean?.trim() ? form.ean : 'Skapas automatiskt vid sparning') : 'Ingen kod så länge GS1 inte är valt'}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Gamla EAN-koder används inte längre. När GS1 är valt tar systemet nästa lediga GTIN-13 automatiskt.</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Utpris till privatkund inkl. moms</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.salesPriceRetailIncVat ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        salesPriceRetailIncVat: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Utpris till företagskund exkl. moms</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.salesPriceDefaultExVat ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        salesPriceDefaultExVat: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Typ</label>
                  <select
                    value={form.kind}
                    onChange={(e) => setForm((current) => ({ ...current, kind: e.target.value }))}
                    className="w-full"
                  >
                    <option value="BULK">BULK</option>
                    <option value="INSTANCE">INSTANCE</option>
                    <option value="SERVICE">SERVICE</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Huvudartikel</label>
                  <select
                    value={form.parentProductId ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        parentProductId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {productOptions
                      .filter((optionProduct) => optionProduct.id !== productId)
                      .map((optionProduct) => (
                        <option key={optionProduct.id} value={optionProduct.id}>
                          {optionProduct.optionLabel}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Kategori</label>
                  <select
                    value={form.categoryItemId ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        categoryItemId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {(valueListItems.PRODUCT_CATEGORY ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Enhet</label>
                  <select
                    value={form.defaultUomItemId ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        defaultUomItemId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {(valueListItems.UOM ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Moms</label>
                  <select
                    value={form.defaultVatRateItemId ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        defaultVatRateItemId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {(valueListItems.VAT_RATES ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">Nettovikt (g)</label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={form.netWeightG ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        netWeightG: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!form.canPurchase}
                      onChange={(e) => setForm((current) => ({ ...current, canPurchase: e.target.checked }))}
                    />
                    Kan köpas in
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!form.canSell}
                      onChange={(e) => setForm((current) => ({ ...current, canSell: e.target.checked }))}
                    />
                    Kan säljas
                  </label>
                </div>

                <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3 md:col-span-2">
                  <div>
                    <h4 className="font-medium text-gray-900">Integrationer</h4>
                    <p className="text-sm text-gray-600">ApisAI är källan. Markera bara de kanaler där produkten verkligen ska finnas.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.syncWooCommerce}
                        onChange={(e) => setForm((current) => ({ ...current, syncWooCommerce: e.target.checked }))}
                      />
                      WooCommerce
                    </label>
                    <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.syncZettle}
                        onChange={(e) => setForm((current) => ({ ...current, syncZettle: e.target.checked }))}
                      />
                      Zettle
                    </label>
                    <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.syncGs1}
                        onChange={(e) => setForm((current) => ({ ...current, syncGs1: e.target.checked }))}
                      />
                      GS1 / EAN
                    </label>
                    <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.syncFortnox}
                        onChange={(e) => setForm((current) => ({ ...current, syncFortnox: e.target.checked }))}
                      />
                      Fortnox
                    </label>
                  </div>
                  <div className="text-xs text-gray-500">
                    WooCommerce och Zettle kräver privatpris inkl. moms. Fortnox kräver företagspris exkl. moms. GS1 allokerar nästa lediga GTIN-13 automatiskt.
                  </div>
                </div>
              </div>

              {isPreparationProduct && (
                <div className="space-y-3 rounded border border-orange-200 bg-orange-50 p-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Preparatdata</h4>
                    <p className="text-sm text-gray-600">Används i kuploggen för karens, behandlingstid och säkerhetsblad.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm text-gray-600">Karenstid (dagar)</label>
                      <input
                        type="number"
                        min={0}
                        value={preparationMetaFromForm(form).withdrawalDays ?? ''}
                        onChange={(e) =>
                          setForm((current) => ({
                            ...current,
                            metaJson: {
                              ...(current.metaJson ?? {}),
                              preparation: {
                                ...preparationMetaFromForm(current),
                                withdrawalDays: e.target.value === '' ? null : Number(e.target.value),
                              },
                            },
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600">Behandlingslängd (dagar)</label>
                      <input
                        type="number"
                        min={0}
                        value={preparationMetaFromForm(form).treatmentLengthDays ?? ''}
                        onChange={(e) =>
                          setForm((current) => ({
                            ...current,
                            metaJson: {
                              ...(current.metaJson ?? {}),
                              preparation: {
                                ...preparationMetaFromForm(current),
                                treatmentLengthDays: e.target.value === '' ? null : Number(e.target.value),
                              },
                            },
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-600">Instruktion / säkerhetsblad URL</label>
                      <input
                        value={preparationMetaFromForm(form).instructionUrl ?? ''}
                        onChange={(e) =>
                          setForm((current) => ({
                            ...current,
                            metaJson: {
                              ...(current.metaJson ?? {}),
                              preparation: {
                                ...preparationMetaFromForm(current),
                                instructionUrl: e.target.value,
                              },
                            },
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h4 className="font-medium">Ingående artiklar</h4>
                    <p className="text-sm text-gray-500">Lägg till emballage eller andra artiklar som ingår i produkten.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setComponentRows((current) => [...current, EMPTY_COMPONENT_ROW])}
                    className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                  >
                    Lägg till rad
                  </button>
                </div>

                <div className="space-y-2">
                  {componentRows.length === 0 && <p className="text-sm text-gray-500">Inga ingående artiklar registrerade.</p>}
                  {componentRows.map((row, index) => {
                    const selectedProduct =
                      row.componentProductId === ''
                        ? null
                        : productOptions.find((optionProduct) => optionProduct.id === Number(row.componentProductId)) ?? null;
                    const isPieceUom = selectedProduct?.uomLabel === 'st';

                    return (
                      <div key={`${index}-${selectedProduct?.id ?? 'empty'}`} className="grid grid-cols-10 items-end gap-2 rounded border border-gray-200 p-3">
                        <div className="relative col-span-5">
                          <label className="block text-sm text-gray-600">Artikel</label>
                          <input
                            type="text"
                            value={row.componentSearch}
                            placeholder="Sök artikelnummer eller namn"
                            onFocus={() => setOpenComponentSearchIndex(index)}
                            onChange={(e) =>
                              updateComponentRow(index, {
                                componentProductId: '',
                                componentSearch: e.target.value,
                              })
                            }
                            onBlur={() => {
                              window.setTimeout(() => {
                                setOpenComponentSearchIndex((current) => (current === index ? null : current));
                                setComponentRows((current) =>
                                  current.map((entry, rowIndex) => {
                                    if (rowIndex !== index || entry.componentProductId === '') {
                                      return entry;
                                    }
                                    const matchedProduct = productOptions.find((optionProduct) => optionProduct.id === Number(entry.componentProductId));
                                    if (!matchedProduct) {
                                      return entry;
                                    }
                                    return {
                                      ...entry,
                                      componentSearch: matchedProduct.optionLabel,
                                    };
                                  })
                                );
                              }, 120);
                            }}
                            className="w-full"
                          />
                          {openComponentSearchIndex === index && (
                            <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                              {productOptions
                                .filter((optionProduct) => optionProduct.id !== productId)
                                .filter((optionProduct) => {
                                  const search = row.componentSearch.trim().toLowerCase();
                                  if (!search) {
                                    return true;
                                  }
                                  const haystack = `${optionProduct.sku ?? ''} ${optionProduct.name}`.toLowerCase();
                                  return haystack.includes(search);
                                })
                                .slice(0, 12)
                                .map((optionProduct) => (
                                  <button
                                    key={optionProduct.id}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      updateComponentRow(index, {
                                        componentProductId: optionProduct.id,
                                        componentSearch: optionProduct.optionLabel,
                                      });
                                      setOpenComponentSearchIndex(null);
                                    }}
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                  >
                                    {optionProduct.optionLabel}
                                  </button>
                                ))}
                              {productOptions
                                .filter((optionProduct) => optionProduct.id !== productId)
                                .filter((optionProduct) => {
                                  const search = row.componentSearch.trim().toLowerCase();
                                  if (!search) {
                                    return true;
                                  }
                                  const haystack = `${optionProduct.sku ?? ''} ${optionProduct.name}`.toLowerCase();
                                  return haystack.includes(search);
                                })
                                .slice(0, 12).length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
                            </div>
                          )}
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm text-gray-600">Mängd</label>
                          <input
                            type="number"
                            min="0"
                            step={isPieceUom ? '1' : '0.01'}
                            value={row.qty}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              if (isPieceUom && nextValue.includes('.')) {
                                return;
                              }
                              updateComponentRow(index, { qty: nextValue });
                            }}
                            className="w-full"
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm text-gray-600">Enhet</label>
                          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            {selectedProduct?.uomLabel ?? '-'}
                          </div>
                        </div>

                        <div className="relative col-span-1">
                          <label className="block select-none text-sm text-transparent">X</label>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteIndex((current) => (current === index ? null : index))}
                            className="flex h-[42px] w-[42px] items-center justify-center rounded border border-red-200 text-red-600 hover:bg-red-50"
                            aria-label="Ta bort rad"
                            title="Ta bort rad"
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18" />
                              <path d="M8 6V4h8v2" />
                              <path d="M19 6l-1 14H6L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                            </svg>
                          </button>
                          {confirmDeleteIndex === index && (
                            <div className="absolute right-0 top-[calc(100%+6px)] z-10 w-56 rounded border border-red-200 bg-white p-2 shadow-lg">
                              <p className="text-xs text-red-700">Vill du verkligen ta bort raden?</p>
                              <div className="mt-2 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteIndex(null)}
                                  className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                >
                                  Nej
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeComponentRow(index)}
                                  className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                                >
                                  Ja
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))}
                  className="min-h-[90px] w-full"
                />
              </div>

              {product.priceHistory.length > 0 && (
                <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Prishistorik</h4>
                    <p className="text-sm text-gray-600">Senaste prisändringarna för aktuell produkt.</p>
                  </div>
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Datum</th>
                          <th className="px-2 py-1.5 text-left">Pris exkl. moms</th>
                          <th className="px-2 py-1.5 text-left">Notering</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.priceHistory.map((row) => (
                          <tr key={row.id} className="border-t border-gray-200">
                            <td className="px-2 py-1.5">{row.changedAt}</td>
                            <td className="px-2 py-1.5">{formatPrice(row.priceExVat)}</td>
                            <td className="px-2 py-1.5">{row.note ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button
                  type="button"
                  onClick={resetEditor}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
                <div className="ml-auto">
                  {!showDeactivateConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeactivateConfirm(true)}
                      className="rounded bg-red-600 px-2 py-1 text-sm text-white hover:bg-red-700"
                    >
                      Inaktivera
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2">
                      <span className="text-sm text-red-700">Vill du verkligen inaktivera produkten?</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete()}
                        disabled={deletingProduct}
                        className="rounded bg-red-600 px-2 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deletingProduct ? 'Inaktiverar...' : 'Ja'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeactivateConfirm(false)}
                        disabled={deletingProduct}
                        className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Nej
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
